import styled from '@emotion/styled';
import { ChangeEvent, ReactNode, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSearchParams } from 'react-router-dom';

import { useAnonymousAnalyticsTrackPage } from '../../common/analytics';
import { IconArrowRight } from '../../common/design-tokens/Icons';
import { ecomSiteURL, membershipHubURL } from '../../common/urls';
import useMoiLoginEnabledFlag from '../../common/useMoiLoginEnabled';
import { validateEmail } from '../../common/utils';
import { t } from '../../localization/LocaleContext';
import { useMoiJwtCookie } from '../../remoteData/unauthenticated';
import Alert from '../_sonic/_molecules/Feedback/Alert/Alert';
import LoadingScreen from '../LoadingScreen';
import FloatingLabelInput from '../shared/FloatingLabelInput';
import { translateError, useXsrf } from './authenticationUtils';
import PasswordField from './PasswordField';
import { Container, SubmitButton, Text, Title } from './styledComponents';

const InputContainer = styled.div`
  margin-top: ${({ theme }) => theme.spacers.xs};
`;

const SubmitContainer = styled.div`
  margin-top: ${({ theme }) => theme.spacers.l};
  display: flex;
  flex-wrap: wrap;
  align-items: center;
`;

const ForgotPasswordContainer = styled.div`
  margin-top: ${({ theme }) => theme.spacers.l};

  a {
    white-space: nowrap;
  }
`;

export default function SignIn() {
  const [searchParams] = useSearchParams();
  const error = searchParams.get('error');
  const { loading: xsrfLoading, error: xsrfError, xsrfToken } = useXsrf();

  const moiLoginEnabled = useMoiLoginEnabledFlag();

  useAnonymousAnalyticsTrackPage({
    name: 'Sign In',
    category: 'Public',
    properties: {
      error,
    },
  });

  if (xsrfError) {
    throw xsrfError;
  }
  if (!xsrfToken || xsrfLoading) {
    return <LoadingScreen />;
  }

  if (moiLoginEnabled) {
    return <MoiLogin />;
  }

  return <LegacyLogin xsrfToken={xsrfToken} />;
}

function MoiLogin() {
  const [searchParams] = useSearchParams();

  // Create the redirectUri for MOI based on the current url.
  // Persist the "next" query param if it exists, and ignore other query params.
  let next = searchParams.get('next') ?? '/dashboard';
  // Also replace root path with /dashboard
  next = ['', '/'].includes(next) ? '/dashboard' : next;
  const signInUrl = window.location
    .toString()
    .replace(window.location.search, '');
  const redirectObj = new URL(signInUrl);
  redirectObj.searchParams.append('next', next);
  const redirectUri = redirectObj.toString();

  const code = searchParams.get('code');
  const state = searchParams.get('state');
  const loginState: 'auth-flow' | 'auth-cookie' = !!code
    ? 'auth-cookie'
    : 'auth-flow';

  return code && state && loginState === 'auth-cookie' ? (
    <MoiAuthCookie
      code={code}
      state={state}
      next={next}
      redirectUri={redirectUri}
    />
  ) : (
    <MoiAuthFlow redirectUri={redirectUri} />
  );
}

function MoiAuthFlow({ redirectUri }: { redirectUri: string }) {
  const authFlowRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (authFlowRef.current) {
      authFlowRef.current.click();
    }
  }, []);

  return (
    <Container narrow>
      <form method="POST" action="/api/auth-flow">
        <Title>Signing in...</Title>
        <Text>
          Click{' '}
          <button
            type="submit"
            ref={authFlowRef}
            className="btn-link text-decoration-underline"
          >
            here
          </button>{' '}
          if you are not redirected automatically.
        </Text>
        <input type="hidden" name="redirect_uri" value={redirectUri} />
      </form>
    </Container>
  );
}

function MoiAuthCookie({
  code,
  state,
  next,
  redirectUri,
}: {
  code: string;
  state: string;
  next: string;
  redirectUri: string;
}) {
  // Use next to create URL to an authenticated path.
  // This is different than redirectUri because it should not contain "/user/*"
  const authenticatedNextUrl = new URL(next, window.location.origin).href;

  const { isError } = useMoiJwtCookie({
    code,
    state,
    redirectUri,
    onSuccess: () => {
      window.location.href = authenticatedNextUrl;
    },
  });

  return isError ? (
    <Container narrow>
      <Title>There was an error signing you in.</Title>
      <Text>
        Please <a href={authenticatedNextUrl}>try again</a> later.
      </Text>
    </Container>
  ) : (
    <LoadingScreen />
  );
}

interface LegacyLoginProps {
  xsrfToken: string;
}

function LegacyLogin({ xsrfToken }: LegacyLoginProps) {
  const [searchParams] = useSearchParams();
  const error = searchParams.get('error');
  const redirectUrl = searchParams.get('next');

  const [email, setEmail] = useState('');
  const [isEmailAddressValid, setIsEmailAddressValid] = useState<
    boolean | undefined
  >(undefined);
  const [password, setPassword] = useState<string>('');
  const intl = useIntl();

  function handleEmailChange(event: ChangeEvent<HTMLInputElement>) {
    const targetEmail = event.target.value.trim();
    setEmail(targetEmail);
    if (!isEmailAddressValid) {
      setIsEmailAddressValid(validateEmail(targetEmail));
    }
  }

  const shouldDisableLogin = !isEmailAddressValid || !password;

  return (
    <Container narrow>
      <Title>{t('login_title')}</Title>
      <Text>
        {t('login_text', {
          link: (chunks: ReactNode) => <a href={ecomSiteURL()}>{chunks}</a>,
        })}
      </Text>
      {error && (
        <Alert status="error" hideClose mb={4}>
          {translateError(error)}
        </Alert>
      )}
      <form method="POST" action="/api/account/login">
        <input type="hidden" name="_xsrf" value={xsrfToken} />
        {redirectUrl && (
          <input type="hidden" name="redirect_url" value={redirectUrl} />
        )}
        <InputContainer>
          <FloatingLabelInput
            type="text"
            name="email"
            value={email}
            error={
              isEmailAddressValid === false
                ? intl.formatMessage({
                    id: 'general_invalid_email',
                  })
                : undefined
            }
            label={intl.formatMessage({
              id: 'general_email_fieldtext',
            })}
            inputProps={{
              'aria-invalid': isEmailAddressValid === false,
              autoComplete: 'email',
            }}
            data-testid="signin-email"
            onChange={handleEmailChange}
            onBlur={() => setIsEmailAddressValid(validateEmail(email))}
          />
        </InputContainer>
        <InputContainer>
          <PasswordField
            name="password"
            value={password}
            label={intl.formatMessage({
              id: 'general_password_fieldtext',
            })}
            onChange={(event) => {
              setPassword(event.target.value);
            }}
            inputProps={{ autoComplete: 'current-password' }}
          />
        </InputContainer>
        <SubmitContainer>
          <SubmitButton
            size="xsmall"
            rounded
            icon="end"
            type="submit"
            disabled={shouldDisableLogin}
          >
            {t('login_submit_button')}
            <IconArrowRight aria-hidden />
          </SubmitButton>
        </SubmitContainer>
      </form>
      <ForgotPasswordContainer>
        <Text>
          {t('general_forgot_password_text_with_hub_link', {
            link: (chunks: ReactNode) => (
              <a
                href={membershipHubURL('/request-password-reset')}
                target="_blank"
                rel="noreferrer"
              >
                {chunks}
              </a>
            ),
          })}
        </Text>
      </ForgotPasswordContainer>
    </Container>
  );
}
